import logger from '@/modules/Logger';
import store from '@/store/index';

export default {
  // publish local stream to channel
  async publishStreamToChannel() {
    this.constraints.cameraId = this.videoDevice || null;
    this.constraints.microphoneId = this.audioDevice || null;
    this.localTracks = [];

    // Bandwidth-adaptive audio-only: override video constraint after device detection
    if (store.state.videoRtc.audioOnly) {
      this.constraints.video = false;
    }

    // Check if new constraints are applied
    logger.debug(`New Device Constraints: ${JSON.stringify(this.constraints)}`);

    await this.createMicrophoneTracks();

    // Wait for VB extension init to complete before checking readiness
    if (this.videoEffect !== 'none' && this.vbInitPromise) {
      await this.vbInitPromise;
    }

    if (this.videoEffect !== 'none' && this.vbProcessorReady) {
      // Extension path: create camera tracks then pipe VB processor
      if (this.constraints.video) {
        await this.createCameraTracks();

        // Blur is best-effort during join: a rejected pipe or enable must not
        // abort the lesson. On failure, log and fall through to publishing the
        // raw camera track so the user still joins (Req 9.2, 9.4).
        try {
          if (this.ensureVbPipelined()) {
            this.vbProcessor.setOptions({ type: 'blur', blurDegree: 2 });
            await this.vbProcessor.enable();
          }
        } catch (error) {
          // Reset effect state to 'none' so getMediaStream / the local tile /
          // the UI all consistently reflect that raw video is being published
          // (getMediaStream only swaps in the processed track when
          // videoEffect !== 'none'). The processor may remain piped-but-disabled,
          // which is harmless while videoEffect is 'none'.
          this.videoEffect = 'none';
          logger.error('[Agora] Failed to apply blur on publish, publishing raw video', {
            operation: 'vbackground:set-effect',
            reason: error.message || error,
            event_type: 79
          });
        }
      }
    } else if (this.videoEffect !== 'none' && !this.vbProcessorReady) {
      // Fallback path: use BodyPix canvas pipeline. Best-effort during join —
      // neither a thrown error NOR a silent failure may abort the lesson or
      // drop video.
      let blurError = null;
      try {
        await this.createVbackgroundTrack();
      } catch (error) {
        blurError = error;
      }

      // createVbackgroundTrack() can also fail QUIETLY: performSegmentation()
      // returning falsy makes it `return` without pushing a track (and without
      // throwing), and a missing canvas element pushes an undefined track. So
      // don't rely on the catch alone — verify a real video track was added and
      // recover with a raw camera track if not. This keeps camera-enabled users
      // from joining audio-only.
      const hasVideoTrack = this.constraints.video
        && this.localTracks.some(track => track && track.trackMediaType === 'video');

      if (this.constraints.video && !hasVideoTrack) {
        // Drop any partial/invalid video entry before recreating the track.
        this.localTracks = this.localTracks.filter(
          track => track && track.trackMediaType !== 'video'
        );
        this.videoEffect = 'none';
        try {
          // createCameraTracks() already pushes the new track into
          // this.localTracks — do NOT push again, or the duplicate video entry
          // makes Agora reject publishing.
          await this.createCameraTracks();
        } catch (cameraError) {
          logger.error('[Agora] Failed to create raw camera track after blur failure', {
            operation: 'vbackground:set-effect',
            reason: cameraError.message || cameraError,
            event_type: 79
          });
        }
        logger.error('[Agora] BodyPix blur produced no video track on publish, publishing raw video', {
          operation: 'vbackground:set-effect',
          reason: blurError ? (blurError.message || blurError) : 'segmentation returned no track',
          event_type: 79
        });
      }
    } else if (this.constraints.video) {
      // No blur: just create camera tracks
      await this.createCameraTracks();
    }

    return await this.publishTracks();
  }
}
